import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import styles from "../../styles/components/ui/SearchSelect.module.css";

function SearchSelect({ 
  label, 
  options = [], 
  value, 
  onChange, 
  required = false, 
  placeholder = "Select...",
  disabled = false,
  error = null,
  maxHeight = "200px",
  showError = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const [hasLostFocus, setHasLostFocus] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Memoize filtered options for better performance
  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lowerSearch = search.toLowerCase();
    return options.filter((opt) => 
      typeof opt === 'string' && opt.toLowerCase().includes(lowerSearch)
    );
  }, [options, search]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && dropdownRef.current && filteredOptions.length > 0) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedIndex, isOpen, filteredOptions.length]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  const handleSelect = useCallback((option) => {
    onChange(option);
    setSearch(""); // Clear search when an option is selected
    closeDropdown();
    inputRef.current?.blur();
    // Mark as touched only when user actually selects something
    if (!hasBeenTouched) {
      setHasBeenTouched(true);
    }
  }, [onChange, closeDropdown, hasBeenTouched]);

  const handleKeyDown = useCallback((e) => {
    // Open dropdown with arrow keys or Enter when closed
    if (!isOpen && ["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => 
          Math.min(prev + 1, filteredOptions.length - 1)
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        closeDropdown();
        inputRef.current?.blur();
        break;
      case "Tab":
        closeDropdown();
        // Mark as touched when user tabs away without selecting
        if (!hasBeenTouched && !value) {
          setHasBeenTouched(true);
        }
        break;
      default:
        break;
    }
  }, [isOpen, filteredOptions, highlightedIndex, handleSelect, closeDropdown, hasBeenTouched, value]);

  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setSearch(val);
    
    // Always open dropdown when typing
    if (!isOpen && val.length > 0) {
      setIsOpen(true);
    }

    // If user clears the input, clear the selection
    if (val === "" && value) {
      onChange("");
    }
  }, [isOpen, value, onChange]);

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
    }
  }, [disabled]);

  const handleInputFocus = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
    }
  }, [disabled]);

  const handleInputBlur = useCallback(() => {
    // Mark as touched only when the field loses focus and user didn't select anything
    if (!hasBeenTouched && !value) {
      setHasBeenTouched(true);
    }
    setHasLostFocus(true);
  }, [hasBeenTouched, value]);

  // Determine if we should show error state
  const shouldShowError = useMemo(() => {
    // Always show if there's an explicit error prop
    if (error) return true;
    
    // Only show required field error when:
    // 1. Field is required AND empty
    // 2. AND (user has lost focus OR we're forcing errors to show)
    if (required && !value && (hasLostFocus || showError)) {
      return true;
    }
    
    return false;
  }, [error, required, value, hasLostFocus, showError]);

  const getInputStyles = () => {
    const baseStyles = {
      color: (value || search) ? 'var(--text-main)' : 'var(--text-muted)',
      cursor: disabled ? 'not-allowed' : 'text',
      opacity: disabled ? 0.6 : 1
    };

    if (shouldShowError) {
      baseStyles.borderColor = 'var(--error)';
    } else if (isOpen) {
      baseStyles.borderColor = 'var(--primary)';
    } else {
      baseStyles.borderColor = 'var(--border)';
    }

    return baseStyles;
  };

  const getOptionStyles = (option, index) => {
    const styles = {};
    
    if (index === highlightedIndex) {
      styles.backgroundColor = 'var(--hover-overlay)';
    } else if (option === value) {
      styles.backgroundColor = 'var(--background-alt)';
      styles.fontWeight = 'var(--font-weight-semibold)';
    } else {
      styles.backgroundColor = 'var(--card-bg)';
    }

    return styles;
  };

  const getDropdownIconStyles = () => {
    return {
      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
      opacity: disabled ? 0.4 : 1,
    };
  };

  const getErrorMessage = () => {
    if (error) return error;
    if (required && !value && (hasLostFocus || showError)) {
      return "This field is required";
    }
    return null;
  };

  const errorMessage = getErrorMessage();

  // Display value in input - show selected value when not searching, show search term when searching
  const displayValue = search !== "" ? search : value;

  return (
    <div className={styles.container} ref={containerRef}>
      {label && (
        <label className={styles.label} htmlFor={`search-select-${label}`}>
          {label}
          {required && <span className={styles.requiredStar}>*</span>}
        </label>
      )}
      
      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          id={`search-select-${label}`}
          type="text"
          value={displayValue || ""}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`dropdown-${label}`}
          aria-invalid={shouldShowError}
          aria-required={required}
          autoComplete="off"
          className={`${styles.input} ${shouldShowError ? styles.inputError : ''}`}
          style={getInputStyles()}
        />
        
        {/* Custom placeholder - only show when there's no value and no search */}
        {!value && !search && (
          <div className={styles.placeholder}>
            {placeholder}
          </div>
        )}

        {/* Dropdown icon */}
        <div 
          className={styles.dropdownIcon}
          style={getDropdownIconStyles()}
        >
          ▼
        </div>
      </div>

      {/* Error message */}
      {errorMessage && (
        <div className={styles.errorMessage}>
          {errorMessage}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          id={`dropdown-${label}`}
          role="listbox"
          className={styles.dropdown}
          style={{ maxHeight }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, index) => (
              <div
                key={`${opt}-${index}`}
                role="option"
                aria-selected={opt === value}
                onClick={() => handleSelect(opt)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={styles.option}
                style={getOptionStyles(opt, index)}
              >
                {opt}
              </div>
            ))
          ) : (
            <div className={styles.noResults}>
              No results found for "{search}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchSelect;